import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate, unstable_useBlocker as useBlocker, useBeforeUnload } from "react-router-dom";

//========== Import components from MUI Library ============
//All Wrapper Elements
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Alert from "@mui/material/Alert";
//All Loading related components
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
//All CSS
import { makeStyles } from "tss-react/mui";
//All Element/ Controls
import Typography from "@mui/material/Typography";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";

// All Icons
import AddIcon from "@mui/icons-material/Add";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

//========== Import components from our files ============
//All Sub Pages for Popup
import InfoSection from "./InfoSection";

// All Common Element/ Controls
import NotAllowedDialog from "../../../../Common/NotAllowedDialog";
import { ButtonUI, ConfirmPopup } from "../../../../Common";
import CommonLayout from "../../../../Common/Layout";
import Tags from "../../Tags";

// Others
import { checkReservedKeywords } from "../../../../../util/restrictedStatement";
import { isEqual, setPrevState } from "../../../../../util/common";

import { clearDataSources, showToast } from "../../../../../store/actions";
import { clearEntities } from "../../../../../store/actions/entity";

import DqmClass from "../../../../../service/dqm";
import DataSourceClass from "../../../../../service/dataSource";
import Workspace from "../../../../../service/workspace";

import { DATA_SOURCE, OBJECT_TYPE, TAGS } from "../../../../../util/Data/DataModeling";
import { TAG_ERRORS } from "../../Tags/data";
import { navDatasets, navEntities } from "../../../../kmk/AppData";
import HelpContextIcon from "../../../../Common/HelpPanel/HelpContextIcon";
import AttributesTables from "./AttributesTables";
import DSContext from "./DSContext";
import SmallToolbar from "../../../../Common/SmallToolbar";

const useStyles = makeStyles()({
  Header: {
    fontSize: "1.1rem",
    minHeight: "60px !important",
    backgroundColor: "#e8edf1",
    color: "black",
    marginTop: 0,
    borderRadius: 0,
    boxShadow: 0,
    fontWeight: 600,
  },
  HeaderTitle: {
    fontSize: 20,
    lineHeight: 1.5,
    paddingTop: 0,
    paddingRight: "20px",
    display: "inline-block",
  },
  container: {
    padding: 0,
    backgroundColor: "#FFFFFF",
  },
  subContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    borderTop: 0,
  },
  PanelContainer: {
    padding: "10px 30px",
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
  Toolbar: {
    fontSize: "1.1rem",
    backgroundColor: "white",
    color: "black",
    display: "flex",
    gap: "10px",
    alignItems: "center",
  },
  deleteReffConfirm: {
    "& span": {
      display: "block",
    },
  },
  resetFiles: {
    paddingTop: "0px !important",
  },
  Alert: {
    paddingTop: 0,
    paddingBottom: 0,
    borderRadius: 20,
  },
});

export default function AddDataSource() {
  const { Workspace_id, dataSourceId } = useParams();

  const config = useSelector((state) => state.config);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const newSource = dataSourceId === "add";

  const { delimiters, nullValues } = config;

  const { classes } = useStyles();
  const defaultSourceValues = config.defaultSourceValues;

  const oldDisplayNameRef = useRef("");
  const workspaceInfo = useRef({
    WorkspaceName: "",
    IsDataVaultEnabled: false,
    accessRole: 0,
  });
  let initialSourceCopy = useRef({});
  let initialConnCopy = useRef({});
  const entities = useRef([]);
  const sources = useRef([]);
  const sourceName = useRef("");

  const oldDisplayName = oldDisplayNameRef.current;
  const { WorkspaceName, IsDataVaultEnabled, accessRole } = workspaceInfo.current;

  const [dsInfoState, setDSInfoState] = useState({
    loading: false,
    displayName: "",
    displayNameError: "",
    name: "",
    currentName: "",
    nameError: "",
    location: "",
    stage: 0,
    locationList: [],
    type: defaultSourceValues.type,
    customDelimeter: "",
    delimiter: defaultSourceValues.delimiter,
    fieldEnclosedBy: defaultSourceValues.qualifier,
    encoding: defaultSourceValues.encoding,
    compressionFormat: defaultSourceValues.compression,
    nullValue: defaultSourceValues.nullValue,
    customNullValue: "",
    hasHeaderRow: "yes",
    path: "",
    description: "",
    connectionType: "",
    container: "",
    schemas: [],
    schema: "",
    tables: [],
    table: { TABLE_TYPE: "", TABLE_NAME: "" },
    DBName: "",
    resetFiles: false,
    shareWithDC: false,
    excludeDQMCritical: false,
    removeMissingDataOnLoad: false,
    datasetType: "tracked",
    onError: defaultSourceValues.errorAction,
    onErrorNumber: 0,
    skipFileError: "",
  });

  //All the states which are initially set and never changes
  const { name: dataSourceName } = dsInfoState;

  const [fields, setFields] = useState([]);
  const [tableDataLoading, setTableDataLoading] = useState(false);
  const [deleteState, setDeleteState] = useState({
    openConfirm: false,
    confirmTitle: "",
    confirmMessage: "",
    handleClick: () => {},
    loadingForConfirm: false,
    confirmBtnName: "",
    confirmBtnColor: "",
  });
  const [notAllowedDialog, setNotAllowedDialog] = useState({
    openConfirm: false,
    confirmMessage: "",
    title: "",
  });
  const [sfErrors, setSfErrors] = useState({
    schema: false,
    table: false,
  });
  const [restrictedObject, setRestrictedObject] = useState({
    schema: "",
    table: {
      TABLE_NAME: "",
      TABLE_TYPE: "",
    },
  });

  const tagsRef = useRef([]);
  const [doNotBlock, setDoNotBlock] = useState(false);

  const dataTableRef = useRef();
  const nameRef = useRef(null);

  const breadcrumbArr = useMemo(
    () => [
      { link: "/my-workspaces", displayName: "Workspaces", active: false },
      { link: `/my-workspaces/${Workspace_id}`, displayName: WorkspaceName, active: false },
      {
        link: `/my-workspaces/${Workspace_id}/${navDatasets.link}`,
        displayName: DATA_SOURCE.plural,
        active: false,
      },
      { link: "#", displayName: newSource ? `Add ${DATA_SOURCE.singular}` : oldDisplayName, active: true },
    ],
    [Workspace_id, newSource, oldDisplayName, WorkspaceName]
  );

  const navigateToDashboard = useCallback(() => {
    navigate(`/my-workspaces/${Workspace_id}/${navDatasets.link}/`);
  }, [Workspace_id, navigate]);

  // Close Delete Confirm Popup
  const handleCancel = useCallback(() => {
    setPrevState(setDeleteState, { openConfirm: false, confirmBtnName: "", confirmBtnColor: "" });
  }, []);

  const loadSnowflakeTables = useCallback(
    async (schema, tableType = "", tableName = "") => {
      setPrevState(setDSInfoState, { loading: true });

      const dataSourceObject = new DataSourceClass();
      const response = await dataSourceObject.getTablesAndViews(Workspace_id, schema);
      if (response === "Unauthorized") navigate(`/my-workspaces/${Workspace_id}`);
      if (response.success) {
        setPrevState(setDSInfoState, { tables: response.data });
        if (tableType !== "" && tableName !== "") {
          const tableExist = response.data.some(
            ({ TABLE_NAME, TABLE_TYPE }) =>
              tableName.length > 0 && tableName === TABLE_NAME && tableType.length > 0 && tableType === TABLE_TYPE
          );
          setPrevState(setSfErrors, {
            table: !tableExist,
          });
          if (!tableExist) {
            setPrevState(setDSInfoState, { table: { TABLE_NAME: "", TABLE_TYPE: "" } });
          }
        }
      }
      setPrevState(setDSInfoState, { loading: false });
    },
    [Workspace_id, navigate]
  );

  // Delete current DataSet and related dependencies
  const deleteDataSource = async () => {
    setPrevState(setDeleteState, { loadingForConfirm: true });

    const response = await new DataSourceClass().deleteDataSource(Workspace_id, dataSourceId);
    if (response.success) {
      dispatch(
        showToast({
          message: response.message,
          type: "success",
        })
      );
      setPrevState(setDeleteState, { openConfirm: false, loadingForConfirm: false });
      setDoNotBlock(true);
      setTimeout(navigateToDashboard, 10);
      return;
    }
    setPrevState(setDeleteState, { openConfirm: false, loadingForConfirm: false });
  };

  // Removing DV keys, DQM rules, Is Deleted flags and Mappings based on conditions
  const updateFieldsForSave = (hasDVKeys = false) => {
    const updatedFields = fields.map((field) => {
      const hasDQM = field.dqm?.required;
      const hasMappings = Object.keys(field?.entity_mapping || {}).length;
      const isDeleteFlag = field.isDeleted;

      if (!hasDQM && !hasMappings && !isDeleteFlag) return field;

      const updatedField = {
        ...field,
        ...(!hasDVKeys && hasDQM ? { dqm: { required: false } } : {}),
        entity_mapping: {},
        isDeleted: "",
      };

      //Updating all the rows
      dataTableRef.current.updateRow(field.name, updatedField);

      return updatedField;
    });

    setFields(updatedFields);

    setPrevState(setDeleteState, { openConfirm: false, loadingForConfirm: false });

    saveDatasourse(updatedFields);
  };

  //Checking if configuratoins valid then saving or showing NotAllowed message
  const isValidConfig = () => {
    let invalidFields = fields.filter((field) => checkReservedKeywords(field.name) === true) || [];
    if (!invalidFields.length) return true;
    setPrevState(setNotAllowedDialog, {
      openConfirm: true,
      title: "Reserved Keyword(s) Detected",
      confirmMessage: (
        <div style={{ width: "100%" }}>
          <span>
            Following attribute name(s) are reserved. Please change them to save the{" "}
            {DATA_SOURCE.singular.toLowerCase()}:
          </span>

          <div style={{ maxHeight: "300px", overflowY: "auto", marginTop: "10px" }}>
            <List sx={{ listStyleType: "disc", pl: 4, background: "#f0f0f0" }} dense={true}>
              {invalidFields.map((field, i) => (
                <ListItem sx={{ display: "list-item", p: 0 }} key={i}>
                  <ListItemText primary={`${field.name}`} />
                </ListItem>
              ))}
            </List>
          </div>
        </div>
      ),
    });
    return false;
  };

  /**
   * check if any changes in dataset screen, this will require checking both ds and conn object
   * @param {Object} datasourceObj
   * @param {Object} connectionObj
   * @return {Boolean} change in dataset
   */
  const isDataSourceChanged = (datasourceObj = {}, connectionObj = {}) => {
    let isConnObjEqual = isEqual(initialConnCopy.current, connectionObj);
    if (!isConnObjEqual) return true;

    return !isEqual(initialSourceCopy.current, datasourceObj);
  };

  /**
   * create config objects from states
   * @returns {Object}
   */
  const createConfigFromState = (_fields = null) => {
    if (!dsInfoState.name) return { dataSource: {}, connectionObj: {} };

    const connectionName = "ds_" + dsInfoState.name;
    const dataSource = {
      connection_ref: connectionName,
      name: dsInfoState.name,
      displayName: dsInfoState.displayName.trim(),
      description: dsInfoState.description,
      fields: _fields || fields || [],
      shareWithDC: dsInfoState.shareWithDC || false,
      excludeDQMCritical: dsInfoState.excludeDQMCritical || false,
      removeMissingDataOnLoad: dsInfoState.removeMissingDataOnLoad || false,
      datasetType: dsInfoState.datasetType ?? "tracked",
      tags: tagsRef?.current?.getTags() || [],
      ...(dsInfoState.location === "SNOWFLAKE"
        ? {
            format: { type: dsInfoState.table.TABLE_TYPE },
          }
        : {
            format: {
              name: `${dsInfoState.name}_FileFormat`,
              type: dsInfoState.type,
              delimiter: dsInfoState.delimiter === "custom" ? dsInfoState.customDelimeter : dsInfoState.delimiter,
              nullValue: dsInfoState.nullValue === "custom" ? dsInfoState.customNullValue : dsInfoState.nullValue,
              fieldEnclosedBy: dsInfoState.fieldEnclosedBy
                ? dsInfoState.fieldEnclosedBy
                : defaultSourceValues.qualifier,
              encoding: dsInfoState.encoding ? dsInfoState.encoding : defaultSourceValues.encoding,
              compression: dsInfoState.compressionFormat,
              header: dsInfoState.hasHeaderRow,
            },
            onError: dsInfoState.onError,
            onErrorNumber: dsInfoState.onErrorNumber,
          }),
      ...(initialSourceCopy?.current?.version ? { version: initialSourceCopy.current.version } : {}),
    };

    const connectionObj = {
      name: connectionName,
      datasetType: dsInfoState.datasetType ?? "tracked",
      ...(dsInfoState.location === "SNOWFLAKE"
        ? {
            location: dsInfoState.location,
            databaseName: dsInfoState.DBName,
            schema: dsInfoState.schema,
            table: dsInfoState.table.TABLE_NAME,
            objectType: dsInfoState.table.TABLE_TYPE,
          }
        : {
            type: dsInfoState.connectionType,
            location: dsInfoState.location,
            path: dsInfoState.path,
            objectType: "File",
            resetFiles: dsInfoState.resetFiles,
          }),
      ...(initialConnCopy?.current?.id ? { id: initialConnCopy.current.id } : {}),
      ...(initialSourceCopy?.current?.version ? { version: initialSourceCopy.current.version } : {}),
    };

    return { dataSource, connectionObj };
  };

  /**
   * Saving dataset
   */
  const saveDatasourse = async (fields = null) => {
    setPrevState(setDSInfoState, { loading: true });

    const { dataSource, connectionObj } = createConfigFromState(fields);
    const DataSource = new DataSourceClass();

    let message = "";
    let redirect = true;
    if (newSource) {
      const response = await DataSource.addDataSource(Workspace_id, { dataSource, connection: connectionObj });
      if (response.success) message = response.message;
      else redirect = false;
    } else {
      const isDSUpdated = isDataSourceChanged(dataSource, connectionObj);
      if (isDSUpdated) {
        //Deleting HealthPercentage and RecCount from json
        dataSource.fields.forEach((field) => {
          if (field?.dqm?.HealthPercentage !== undefined) delete field.dqm.HealthPercentage;
          if (field?.dqm?.RecCount !== undefined) delete field.dqm.RecCount;
        });

        const response = await DataSource.updateDataSource(Workspace_id, dataSourceId, {
          dataSource,
          connection: connectionObj,
        });
        if (response.success) message = response.message;
        else redirect = false;
      } else {
        redirect = false;
        message = "No changes detected, nothing to save";
      }
    }

    message !== "" &&
      dispatch(
        showToast({
          message: message,
          type: redirect ? "success" : "error",
        })
      );

    setPrevState(setDSInfoState, { loading: false });

    if (redirect) {
      setDoNotBlock(true);
      setTimeout(navigateToDashboard, 10);
      return;
    }
  };

  //Save click
  const onSave = () => {
    setPrevState(setDSInfoState, { nameError: "", displayNameError: "" });
    if (dsInfoState.displayName.trim().length > 50 || dsInfoState.displayName.trim().length < 2)
      setPrevState(setDSInfoState, { displayNameError: "Display name must be between 2 to 50 characters." });
    else if (dsInfoState.name.length > 50 || dsInfoState.name.length < 2)
      setPrevState(setDSInfoState, { nameError: "System name must be between 2 to 50 characters." });
    else if (
      (!oldDisplayNameRef.current ||
        dsInfoState.displayName.trim().toLowerCase() !== oldDisplayNameRef?.current?.trim().toLowerCase()) &&
      sources.current.some(
        (source) => source.displayName?.toLowerCase() === dsInfoState.displayName.trim().toLowerCase()
      )
    )
      setPrevState(setDSInfoState, { displayNameError: "Display name must be Unique." });
    else if (checkReservedKeywords(dsInfoState.name)) {
      setPrevState(setDSInfoState, { nameError: "Reserved keyword." });
    } else if (
      (!sourceName.current || sourceName.current !== dsInfoState.name) &&
      sources.current.some((source) => source.name.toLowerCase() === dsInfoState.name.toLowerCase())
    ) {
      const source = sources.current.find(
        (source) => source.name.toLowerCase() === dsInfoState.name.toLowerCase()
      ).name;
      setPrevState(setDSInfoState, { nameError: `System name must be Unique. (In use for Source: ${source})` });
    } else if (
      (!sourceName.current || sourceName.current !== dsInfoState.name) &&
      entities.current.some((entity) => entity.name.toLowerCase() === dsInfoState.name.toLowerCase())
    ) {
      const entity = entities.current.find(
        (entity) => entity.name.toLowerCase() === dsInfoState.name.toLowerCase()
      ).name;
      setPrevState(setDSInfoState, { nameError: `System name must be Unique. (In use for Entity: ${entity})` });
    } else {
      if (!isValidConfig()) return;
      const { keyLimitationError, repetitiveKeyError } = tagsRef.current.anyErrorInTags();

      if (keyLimitationError) return dispatch(showToast({ message: TAG_ERRORS.EMTPY_KEY, type: "error" }));
      if (repetitiveKeyError) return dispatch(showToast({ message: TAG_ERRORS.DUPLICATE_KEY, type: "error" }));

      const hasDvKey = fields.some((field) => field.dataVaultKey);
      const hasDQM = fields.some((field) => field.dqm?.required);

      const hasMappings = fields.some((field) => field.entity_mapping && Object.keys(field.entity_mapping).length);
      const hasDeletedFlag = fields.some((field) => !!field.isDeleted);

      if (dsInfoState.datasetType === "tracked" && !hasDvKey) {
        return dispatch(showToast({ message: "Data vault key is required for tracked dataset!", type: "error" }));
      }

      if (
        ["untracked", "readonly"].includes(dsInfoState.datasetType) &&
        (hasMappings || hasDeletedFlag || (!hasDvKey && hasDQM))
      ) {
        const dqmNotAllowed = !hasDvKey && hasDQM;
        const datasetIs = dsInfoState.datasetType === "readonly" ? "Read-Only Data View" : "Untracked";
        const removeMessage = dqmNotAllowed ? "DQM, mappings, and the deletion flag" : "the mappings and deletion flag";

        setPrevState(setDeleteState, {
          openConfirm: true,
          confirmTitle: dsInfoState.datasetType === "readonly" ? "Read-Only Data View Error" : "Untracked Error",
          confirmMessage: (
            <span className={classes.deleteReffConfirm}>
              <span>{`Mappings and the deletion flag cannot be set on ${datasetIs} datasets.`}</span>
              {dqmNotAllowed && <span>DQM is only supported when a Data Vault Key is defined.</span>}
              <span>{`Do you want to remove ${removeMessage}, and then save the dataset?`}</span>
            </span>
          ),
          handleClick: () => updateFieldsForSave(hasDvKey),
          confirmBtnName: "Ok",
          confirmBtnColor: "",
        });

        return;
      }

      saveDatasourse();
    }
  };

  const checkForChanges = () => {
    if (newSource || doNotBlock || accessRole === 3 || tableDataLoading) return false;

    const { dataSource, connectionObj } = createConfigFromState();
    return isDataSourceChanged(dataSource, connectionObj);
  };

  let blocker = useBlocker(() => {
    if (checkForChanges()) {
      return !window.confirm("You have unsaved changes. Are you sure you want to leave this page?");
    }
    return false;
  });

  useBeforeUnload((event) => {
    if (checkForChanges()) {
      event.preventDefault();
      return (event.returnValue = "");
    }
  });

  useEffect(() => {
    setPrevState(setDSInfoState, { loading: true });

    new Workspace()
      .getInfo(Workspace_id)
      .then(async (response) => {
        if (response.success) {
          const workspace = response.data;

          workspaceInfo.current = {
            WorkspaceName: workspace.WorkspaceName,
            accessRole: workspace.accessRole,
            IsDataVaultEnabled: workspace.IsDataVaultEnabled,
          };

          let loadData = {
            DBName: workspace.DBName,
          };

          await Promise.all([
            getLocationList(loadData, workspace.WorkspaceName, workspace.IsDataVaultEnabled),
            loadSnowflakeSchemas(loadData, workspace.IsDataVaultEnabled),
            getObjects(),
          ]);

          if (newSource) setPrevState(setDSInfoState, { ...loadData, loading: false });
          else {
            const dataSourceObject = new DataSourceClass();
            const response = await dataSourceObject.getObjectByID(Workspace_id, 1, dataSourceId);

            if (response.success) {
              const { source: dataSource, connection } = JSON.parse(response.data.object);

              if (!dataSource || !connection) {
                setPrevState(setDSInfoState, { ...loadData, loading: false });
                dispatch(
                  showToast({
                    message: `${DATA_SOURCE.singular} does not exists.`,
                    type: "error",
                  })
                );
                return navigate(`/my-workspaces/${Workspace_id}/${navDatasets.link}`);
              }

              if (tagsRef?.current) tagsRef.current.setTagsFromParent(dataSource?.tags || []);

              //  get initial JSON of perticular Source by Name to track changes later
              delete dataSource.id;
              delete dataSource.updatedAt;
              initialSourceCopy.current = JSON.parse(JSON.stringify(dataSource));

              // get initial JSON of perticular connections by Name to track changes later
              delete connection.updatedAt;
              initialConnCopy.current = JSON.parse(JSON.stringify(connection));

              oldDisplayNameRef.current = dataSource.displayName;
              sourceName.current = dataSource.name;

              loadData = {
                ...loadData,
                name: dataSource.name,
                displayName: dataSource.displayName,
                currentName: dataSource.name,
                description: dataSource.description,
                type: dataSource.format?.type || defaultSourceValues.type,
                delimiter: dataSource.format?.delimiter || defaultSourceValues.delimiter,
                nullValue: dataSource.format?.nullValue || defaultSourceValues.nullValue,
                compressionFormat: dataSource.format?.compression || defaultSourceValues.compression,
                fieldEnclosedBy: dataSource.format?.fieldEnclosedBy || defaultSourceValues.qualifier,
                encoding: dataSource.format?.encoding || defaultSourceValues.encoding,
                hasHeaderRow: dataSource.format?.header,
                shareWithDC: dataSource?.shareWithDC || false,
                excludeDQMCritical: dataSource?.excludeDQMCritical || false,
                removeMissingDataOnLoad: dataSource?.removeMissingDataOnLoad || false,
                datasetType: dataSource?.datasetType ?? "tracked",
                onError: dataSource?.onError || defaultSourceValues.errorAction,
                onErrorNumber: dataSource?.onErrorNumber || 0,
              };

              /* Added logic for custom option selection in Delimiters dropdown */
              if (!delimiters.some((obj) => obj.value === dataSource.format?.delimiter)) {
                loadData.delimiter = "custom";
                loadData.customDelimeter = dataSource.format?.delimiter;
              }

              /* Added logic for custom null values selection in null values dropdown */
              if (
                ![undefined, null].includes(dataSource.format?.nullValue) &&
                !nullValues?.some((obj) => obj.value === dataSource.format?.nullValue)
              ) {
                loadData.nullValue = "custom";
                loadData.customNullValue = dataSource.format?.nullValue;
              }

              //Setting Connection
              loadData.location = connection.location;
              loadData.path = connection.path && connection.path[0] !== "/" ? "/" + connection.path : connection.path;
              loadData.resetFiles = connection.resetFiles || false;

              if (
                connection.location === "SNOWFLAKE" &&
                (connection.objectType === "Table" || connection.objectType === "View") &&
                loadData.allSchemas.includes(connection.schema) &&
                loadData.schemas.includes(connection.schema)
              ) {
                loadSnowflakeTables(connection.schema, connection.objectType, connection.table);
              }

              if (connection.location === "SNOWFLAKE") {
                const schemaExist = loadData.allSchemas.includes(connection.schema);
                loadData.schema = schemaExist ? connection.schema : "";

                if (!schemaExist) {
                  setPrevState(setSfErrors, {
                    schema: true,
                  });
                } else if (!loadData.schemas.includes(connection.schema)) {
                  setRestrictedObject({
                    schema: connection.schema,
                    table: { TABLE_TYPE: connection.objectType, TABLE_NAME: connection.table },
                  });
                }

                loadData.table =
                  (connection.objectType === "Table" || connection.objectType === "View") && schemaExist
                    ? { TABLE_TYPE: connection.objectType, TABLE_NAME: connection.table }
                    : { TABLE_TYPE: "", TABLE_NAME: "" };
              }

              setPrevState(setDSInfoState, { ...loadData, loading: false });

              setFields([]);
              setTableDataLoading(true);
              dataTableRef.current.resetData();

              //Getting dqm data for fields
              if (dataSource.fields.length) {
                const dqmClass = new DqmClass();
                const dqmAttributeResult = await dqmClass.getDqmAttributeData(
                  Workspace_id,
                  dataSource.name,
                  dataSource.fields.map(({ name }) => name)
                );

                //Adding dqm data into fields
                if (dqmAttributeResult.success && dqmAttributeResult.data.length) {
                  const dqmAttrMap = new Map(dqmAttributeResult.data.map((i) => [i.Attribute, i]));

                  dataSource.fields.forEach((field) => {
                    if (dqmAttrMap.has(field.name)) {
                      if (!field.dqm) field.dqm = {};

                      field.dqm.HealthPercentage = dqmAttrMap.get(field.name).HealthPercentage;
                      field.dqm.RecCount = dqmAttrMap.get(field.name).RecCount;
                    }
                  });
                }

                initialSourceCopy.current = {
                  ...initialSourceCopy.current,
                  fields: JSON.parse(JSON.stringify([...dataSource.fields])),
                };

                dataTableRef.current.addRow(dataSource.fields);
              }
              setTableDataLoading(false);
            } else {
              setDoNotBlock(true);
              setTimeout(navigateToDashboard, 10);
              return;
            }
          }
        } else {
          setDoNotBlock(true);
          setTimeout(navigateToDashboard, 10);
          return;
        }
      })
      .catch(() => navigate("/my-workspaces"));

    //Get Location List for "Location" Dropdown
    const getLocationList = async (loadData, WorkspaceName, IsDataVaultEnabled) => {
      const dataSourceObject = new DataSourceClass();
      const locations = await dataSourceObject.getLocationList(Workspace_id);

      // Adding entry for current workspace option
      const snowflakeLocation = {
        Stage_id: "SNOWFLAKE",
        Name: "SNOWFLAKE",
        label: WorkspaceName + " (Current Workspace DB)",
        Detail: {
          type: "snowflake",
        },
      };

      const filteredResult = locations.filter(
        (item) =>
          !["salesforce", "sftp", "sharepoint", "mysql", "veevavault", "snowflake"].includes(
            item.Detail.type.toLowerCase()
          )
      );

      if (filteredResult?.length) {
        loadData.locationList = IsDataVaultEnabled ? [snowflakeLocation, ...filteredResult] : [...filteredResult];
      }

      return true;
    };

    const loadSnowflakeSchemas = async (loadData, IsDataVaultEnabled) => {
      if (IsDataVaultEnabled) {
        const dataSourceObject = new DataSourceClass();
        const response = await dataSourceObject.getSchemas(Workspace_id);
        if (response === "Unauthorized") navigate(`/my-workspaces/${Workspace_id}`);
        if (response.success) {
          loadData.schemas = response.data.schemasWithReadAndWrite;
          loadData.allSchemas = response.data.allSchemas;
        }
      }
      return true;
    };

    const getObjects = async () => {
      const datamodeling = new DataSourceClass();
      const { success, data } = await datamodeling.getAllObjectNames(Workspace_id);
      if (!success) return navigate(`/my-workspaces/${Workspace_id}/${navEntities.link}/`);
      sources.current = data.sources;
      entities.current = data.entities;
    };
  }, [
    newSource,
    dataSourceId,
    Workspace_id,
    dispatch,
    navigate,
    loadSnowflakeTables,
    defaultSourceValues.type,
    defaultSourceValues.qualifier,
    defaultSourceValues.encoding,
    defaultSourceValues.delimiter,
    defaultSourceValues.compression,
    delimiters,
    defaultSourceValues.errorAction,
    nullValues,
    defaultSourceValues.nullValue,
    navigateToDashboard,
  ]);

  //Settng Stage id when location gets changed
  useEffect(() => {
    if (dsInfoState.location && dsInfoState.locationList.length) {
      const Stage = dsInfoState.locationList.find((loc) => loc.Name === dsInfoState.location);

      if (Stage)
        setPrevState(setDSInfoState, {
          stage: Stage.Stage_id,
          connectionType: Stage.Detail.type,
          container: Stage.Detail.container,
        });
    }
  }, [dsInfoState.location, dsInfoState.locationList]);

  useEffect(() => {
    return () => {
      if (blocker.state === "blocked") blocker.reset();
      dispatch(clearDataSources());
      dispatch(clearEntities());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const $hasDVKey = useMemo(() => fields.some((field) => field.dataVaultKey), [fields]);

  const handleAddTag = useCallback(() => {
    tagsRef.current.handleAddTag();
  }, []);

  const isSaveDisabled = useMemo(
    () =>
      (dsInfoState.location === "SNOWFLAKE"
        ? !(
            dsInfoState.name &&
            dsInfoState.displayName &&
            (!dsInfoState.location ||
              (dsInfoState.location &&
                dsInfoState.schema &&
                dsInfoState.table.TABLE_TYPE !== "" &&
                dsInfoState.table.TABLE_NAME !== ""))
          )
        : !(
            dsInfoState.name &&
            dsInfoState.displayName &&
            dsInfoState.type &&
            (dsInfoState.delimiter === "custom" ? dsInfoState.customDelimeter : dsInfoState.delimiter) &&
            dsInfoState.onError &&
            !dsInfoState.skipFileError &&
            (dsInfoState.nullValue === "custom" ? dsInfoState.customNullValue : dsInfoState.nullValue) &&
            (!dsInfoState.location || (dsInfoState.location && dsInfoState.path))
          )) ||
      tableDataLoading ||
      (dsInfoState.datasetType === "tracked" && !$hasDVKey),
    [
      dsInfoState.location,
      dsInfoState.name,
      dsInfoState.displayName,
      dsInfoState.schema,
      dsInfoState.table.TABLE_TYPE,
      dsInfoState.table.TABLE_NAME,
      dsInfoState.type,
      dsInfoState.delimiter,
      dsInfoState.customDelimeter,
      dsInfoState.onError,
      dsInfoState.skipFileError,
      dsInfoState.nullValue,
      dsInfoState.customNullValue,
      dsInfoState.path,
      dsInfoState.datasetType,
      tableDataLoading,
      $hasDVKey,
    ]
  );

  const dsContextValue = useMemo(
    () => ({
      loading: dsInfoState.loading,
      dataSourceId,
      fields,
      setFields,
      location: dsInfoState.location,
      name: dsInfoState.name,
      datasetType: dsInfoState.datasetType,
    }),
    [dataSourceId, dsInfoState.datasetType, dsInfoState.loading, dsInfoState.location, dsInfoState.name, fields]
  );

  return (
    <CommonLayout title="Data Modeling" breadcrumbArr={breadcrumbArr} loading={dsInfoState.loading}>
      <Box
        component="main"
        sx={{
          backgroundColor: (theme) => {
            return theme.palette.TiffanyBlue.main;
          },
          flexGrow: 1,
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Backdrop
          sx={{ backgroundColor: "transparent", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={dsInfoState.loading}
        >
          <CircularProgress color="primary" />
        </Backdrop>
        <AppBar
          sx={{
            position: "relative",
            boxShadow: 0,
            backgroundColor: "#e8edf1",
          }}
        >
          <Toolbar className={classes.Header}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography className={classes.HeaderTitle} gutterBottom component="div">
                {`${newSource ? "Add" : "Edit"} ${DATA_SOURCE.singular}`}
              </Typography>
            </Box>
            <Box sx={{ display: "inline-flex", flex: 1 }}>
              {[1, 2].includes(accessRole) && (
                <ButtonUI variant="contained" color="success" onClick={onSave} disabled={isSaveDisabled}>
                  Save
                </ButtonUI>
              )}
              <ButtonUI style={{ marginLeft: 15 }} variant="contained" color="secondary" onClick={navigateToDashboard}>
                Cancel
              </ButtonUI>
              {!newSource && [1, 2].includes(accessRole) && (
                <ButtonUI
                  style={{ marginLeft: 15 }}
                  variant="contained"
                  color="error"
                  onClick={(e) => {
                    if (fields.some((field) => field.referenceTo?.length)) {
                      setPrevState(setNotAllowedDialog, {
                        openConfirm: true,
                        title: "Action not allowed",
                        confirmMessage: (
                          <span className={classes.deleteReffConfirm}>
                            <span>
                              You are trying to delete a {DATA_SOURCE.singular} which contains attributes which are
                              being referenced to {DATA_SOURCE.plural.toLowerCase()}.
                            </span>
                            <span>You need to remove those references to perform this action.</span>
                          </span>
                        ),
                      });
                      return;
                    }

                    setPrevState(setDeleteState, {
                      openConfirm: true,
                      confirmTitle: `Delete ${DATA_SOURCE.singular}`,
                      confirmMessage: `Are you sure you want to delete “${oldDisplayName || dataSourceName}”?`,
                      handleClick: deleteDataSource,
                      confirmBtnName: "",
                      confirmBtnColor: "",
                    });
                  }}
                  disabled={tableDataLoading}
                >
                  Delete
                </ButtonUI>
              )}
              <Box sx={{ display: "inline-flex", alignItems: "center", marginRight: "auto" }}>
                <HelpContextIcon screen={`Add ${DATA_SOURCE.singular}`} />
              </Box>
              <Box className={classes.HeaderTitle} sx={{ display: "inline-flex", alignItems: "end" }}>
                <Alert
                  className={classes.Alert}
                  variant="outlined"
                  icon={<InfoOutlinedIcon fontSize="inherit" />}
                  severity="info"
                >
                  Changes require 'Apply to Data Warehouse' to take effect.
                </Alert>
              </Box>
            </Box>
          </Toolbar>
        </AppBar>

        <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
          <>
            <Box className={classes.subContainer} sx={{ width: "100%" }}>
              <Grid container direction="column" justifyContent="flex-end" alignItems="left">
                <Grid className={classes.PanelContainer}>
                  <SmallToolbar>Info</SmallToolbar>
                  <InfoSection
                    nameRef={nameRef}
                    dsInfoState={dsInfoState}
                    restrictedObject={restrictedObject}
                    sfErrors={sfErrors}
                    setRestrictedObject={setRestrictedObject}
                    setSfErrors={setSfErrors}
                    setDSInfoState={setDSInfoState}
                    setDeleteState={setDeleteState}
                    loadSnowflakeTables={loadSnowflakeTables}
                    fieldsLength={fields.length}
                    addAllFields={(fieldsArr) => {
                      if (fields.some((field) => field.referenceTo?.length)) {
                        setPrevState(setNotAllowedDialog, {
                          openConfirm: true,
                          title: "Action not allowed",
                          confirmMessage: (
                            <span className={classes.deleteReffConfirm}>
                              <span>
                                You are trying to override parent attributes which are being referenced to{" "}
                                {DATA_SOURCE.plural.toLowerCase()}.
                              </span>
                              <span>You need to remove those references to perform this action.</span>
                            </span>
                          ),
                        });
                        return;
                      }

                      setFields([]);
                      dataTableRef.current.resetData();
                      dataTableRef.current.addRow(fieldsArr);
                    }}
                    IsDataVaultEnabled={IsDataVaultEnabled}
                    accessRole={accessRole}
                    hasDVKey={$hasDVKey}
                    sources={sources.current}
                    entities={entities.current}
                  />
                </Grid>
              </Grid>
            </Box>
            <Box className={classes.subContainer} sx={{ marginBottom: "80px", width: "100%" }}>
              <DSContext.Provider value={dsContextValue}>
                <AttributesTables
                  Workspace_id={Workspace_id}
                  accessRole={accessRole}
                  tableDataLoading={tableDataLoading}
                  setDSInfoState={setDSInfoState}
                  IsDataVaultEnabled={IsDataVaultEnabled}
                  newSource={newSource}
                  WorkspaceName={WorkspaceName}
                  dataTableRef={dataTableRef}
                  tagsRef={tagsRef}
                />
              </DSContext.Provider>

              <Grid container direction="column" justifyContent="flex-end" alignItems="left">
                <Grid className={classes.PanelContainer}>
                  <SmallToolbar>{TAGS.plural}</SmallToolbar>
                  <ButtonUI
                    endIcon={<AddIcon />}
                    variant="contained"
                    color="info"
                    style={{ width: "150px" }}
                    onClick={handleAddTag}
                    disabled={dsInfoState.loading || tableDataLoading}
                  >
                    {`Add ${TAGS.singular}`}
                  </ButtonUI>
                </Grid>
                <Grid className={classes.PanelContainer}>
                  <Tags ref={tagsRef} objectType={OBJECT_TYPE.DataSource} />
                </Grid>
              </Grid>
              {deleteState.openConfirm && (
                <ConfirmPopup
                  configData={{
                    popupTitle: deleteState.confirmTitle,
                    popupMessage: deleteState.confirmMessage,
                    handleCancel: deleteState.handleCancel || handleCancel,
                    handleClick: deleteState.handleClick,
                    loadingForConfirm: deleteState.loadingForConfirm,
                    confirmBtnName: deleteState.confirmBtnName,
                    confirmBtnColor: deleteState.confirmBtnColor,
                  }}
                />
              )}
              {notAllowedDialog.openConfirm && (
                <NotAllowedDialog
                  notAllowedDialog={notAllowedDialog}
                  onClose={() => {
                    setNotAllowedDialog({
                      openConfirm: false,
                      confirmMessage: "",
                      title: "",
                    });
                  }}
                />
              )}
            </Box>
          </>
        </Box>
      </Box>
    </CommonLayout>
  );
}
